import { Button, Form, Select, Space, Table, theme } from "antd";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import EditableCell from "../../action-table/editable-cell";
import { DndContext, DragEndEvent, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { CSS } from '@dnd-kit/utilities';
import EditableContext from "../../action-table/editable-context";
import { HolderOutlined } from "@ant-design/icons";
import { SyntheticListenerMap } from "@dnd-kit/core/dist/hooks/utilities";
import { useSetting } from "../../util/settings";

export const useDefaultTasksSetting = () => {
  return useSetting("default-tasks", "application", [
    {
      id: "customer-created",
      label: "Kunde erstellt",
      items: [
        {
          text: "Vertrag eingegangen",
          delay: 6
        }
      ],
    },
    {
      id: "employee-created",
      label: "Mitarbeiter erstellt",
      items: [
        {
          text: "Vertrag eingegangen",
          delay: 6
        }
      ],
    }
  ])
}

interface RowProps extends React.HTMLAttributes<HTMLTableRowElement> {
  'data-row-key': string;
}

interface RowContextProps {
  setActivatorNodeRef?: (element: HTMLElement | null) => void;
  listeners?: SyntheticListenerMap;
}

const RowContext = createContext<RowContextProps>({});

const Row: React.FC<Readonly<RowProps>> = (props) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging, setActivatorNodeRef } = useSortable({
    id: props['data-row-key'],
  });

  const style: React.CSSProperties = {
    ...props.style,
    transform: CSS.Translate.toString(transform),
    transition,
    cursor: 'move',
    ...(isDragging ? { position: 'relative', zIndex: 9999 } : {}),
  };

  const contextValue = useMemo<RowContextProps>(
    () => ({ setActivatorNodeRef, listeners }),
    [setActivatorNodeRef, listeners],
  );

  const [form] = Form.useForm();
  return (
    <Form form={form} component={false}>
      <EditableContext.Provider value={form}>
        <RowContext.Provider value={contextValue}>
          <tr {...props} ref={setNodeRef} style={style} {...attributes} {...listeners} />
        </RowContext.Provider>
      </EditableContext.Provider>
    </Form>
  );
};

const DragHandle: React.FC = () => {
  const { setActivatorNodeRef, listeners } = useContext(RowContext);
  return (
    <Button
      type="text"
      size="small"
      icon={<HolderOutlined />}
      style={{ cursor: 'move' }}
      ref={setActivatorNodeRef}
      {...listeners}
    />
  );
};

const TableFormItem = ({value, onChange, enumId}: any) => {
  const sortedValue = useMemo(() => value?.sort((a, b) => a.orderIdx - b.orderIdx) || [], [value])
  const keyedValue = useMemo(() => sortedValue?.map((v, idx) => ({...v, key: idx})) || [], [sortedValue])

  const addRow = useCallback(() => {
    const newRows = [...(value || []), { label: '', value: ' ', referencedEnum: { id: enumId } }]
    onChange(newRows);
  }, [value, onChange, enumId])

  const deleteRow = useCallback((row) => {
    const newRows = value.filter((_, idx) => idx !== row.key);
    //console.log("Deleted row", row, newRows);
    onChange(newRows);
  }, [value, onChange])

  const handleSaveItem = useCallback((item) => {
    const newItem = {...item}
    newItem.text = newItem.text.trim();
    const newRows = value.map((i, idx) => idx === newItem.key ? newItem : i);
    //console.log("new rows", newRows)
    onChange(newRows);
  }, [value, onChange])

  const columns = useMemo(() => [
    { 
      key: 'sort',
      dataIndex: 'id',
      width: 80, 
      render: () => <DragHandle /> 
    },
    {
      title: 'Beschreibung',
      dataIndex: 'text',
      key: 'text',
      onCell: record => ({
        record,
        editable: true,
        dataIndex: 'text',
        title: 'Beschreibung',
        handleSave: handleSaveItem,
        type: 'string',
      })
    },
    {
      title: 'nach Tagen',
      dataIndex: 'delay',
      key: 'delay',
      type: 'number',
      editable: true,
      onCell: record => ({
        record,
        editable: true,
        dataIndex: 'delay',
        title: 'nach Tagen',
        handleSave: handleSaveItem,
        type: 'number',
      })
    },
    {
      title: 'Aktionen',
      render: (item) => <Button onClick={() => deleteRow(item)} danger>Löschen</Button>
    } 
  ], [handleSaveItem, deleteRow]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        // https://docs.dndkit.com/api-documentation/sensors/pointer#activation-constraints
        distance: 1,
      },
    }),
  );

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (active.id !== over?.id) {
      const activeIndex = keyedValue.findIndex((i) => i.key === active.id);
      const overIndex = keyedValue.findIndex((i) => i.key === over?.id);
      const newList = arrayMove(keyedValue, activeIndex, overIndex).map((v: any, orderIdx) => ({ ...v, orderIdx }))
      onChange(newList);
    }
  };

  return (
    <>
      <div style={{ width: "100%", textAlign: "right" }}><Button type="primary" onClick={addRow}>Neues Todo</Button></div>
      <DndContext sensors={sensors} modifiers={[restrictToVerticalAxis]} onDragEnd={onDragEnd}>
        <SortableContext
          // rowKey array
          items={keyedValue.map((i) => i.key)}
          strategy={verticalListSortingStrategy}
        >
          <Table
            size="small" 
            components={{
              body: { 
                row: Row,
                cell: EditableCell,
              },
            }}
            rowKey="key"
            columns={columns}
            dataSource={keyedValue}
            pagination={false}
          />
        </SortableContext>
      </DndContext>
    </>
  );
}

function DefaultTasks() {
  const [form] = Form.useForm()
  const [selectedEvent, setSelectedEvent] = useState<string | null>(null);
  const [savingChanges, setSavingChanges] = useState<boolean>(false);

  //const { data: events } = useGetEnumsQuery({ st: JSON.stringify({ label: "asc" })}) as any;
  const [events, updateEvents] = useDefaultTasksSetting();
  
  //const { data: tasks } = useGetEnumItemsQuery({ f: JSON.stringify({ "referencedEnum.id": { c: "EQ", v: selectedEvent } }), st: JSON.stringify({ orderIdx: "asc"})}, { skip: !selectedEvent }) as any;
  const tasks = useMemo(() => ({ items: events?.find(e => e.id === selectedEvent)?.items }), [events, selectedEvent]);

  //console.log("default tasks settings", events, events?.filter(e => e.id === selectedEvent), tasks, selectedEvent)

  useEffect(() => {
    form.setFieldsValue({ items: tasks?.items })
  }, [form, tasks?.items])

  const { token } = theme.useToken();

  const formStyle: React.CSSProperties = {
      maxWidth: 'none',
      background: token.colorFillAlter,
      borderRadius: token.borderRadiusLG,
      padding: 24,
    };

  const onFinish = useCallback(async(values) => {
    setSavingChanges(true)
    const newEvents = events.map(e => e.id === selectedEvent ? {...e, items: values.items.map(t => ({ text: t.text, delay: t.delay }))} : e)
    updateEvents(newEvents)
    setSavingChanges(false);
  }, [events, selectedEvent, updateEvents])

  return (
    <>
    <h2>Todos</h2>
    
    <Space direction="vertical" style={{ width: '100%' }}>
      <Select style={{ width: "20em" }} options={events?.map(e => ({ label: e.label, value: e.id }))} value={selectedEvent} onChange={setSelectedEvent} />
      { selectedEvent && 
      <Form form={form} name="default-tasks" style={formStyle} onFinish={onFinish}>
        <Form.Item 
          name="items"
          labelCol={{ span: 0 }}
          wrapperCol={{ span: 24 }}>
            <TableFormItem enumId={selectedEvent} />
        </Form.Item>
        <div style={{ textAlign: 'right' }}>
          <Space size="small">
            <Button loading={savingChanges} type="primary" htmlType="submit">
              Speichern
            </Button>
          </Space>
        </div>
      </Form>
      }
    </Space>
    </>
  )
}

export default DefaultTasks;